import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { OnboardingStepProps } from './types';
import { useToast } from '@/hooks/use-toast';
import { Download, Maximize2, Minimize2, FileText } from 'lucide-react';

interface TermsViewerProps extends OnboardingStepProps {
  title: string;
  content: string;
  acceptLabel: string;
}

const TermsViewer: React.FC<TermsViewerProps> = ({
  title,
  content,
  acceptLabel,
  onNext,
  onBack,
  isFirstStep,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Reset state when content changes
  useEffect(() => {
    setAccepted(false);
    
    // Scroll to top when new content loads
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [content, title]);

  const handleAccept = () => {
    if (!accepted) {
      toast({
        title: "Please Accept Terms",
        description: `Please check the box to accept the ${title}.`,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: `${title} Accepted`,
      description: "Proceeding to the next step...",
    });
    onNext();
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: sans-serif; line-height: 1.6; padding: 20px; }
              h1 { color: #333; }
              pre { white-space: pre-wrap; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm min-h-[80vh] flex flex-col">
      <div className="p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-vitl-green" />
            <h2 className="text-lg font-semibold text-gray-900">Legal Document</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-2"
            >
              {isFullscreen ? (
                <>
                  <Minimize2 className="h-4 w-4" />
                  Exit Fullscreen
                </>
              ) : (
                <>
                  <Maximize2 className="h-4 w-4" />
                  Fullscreen
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      <div 
        ref={contentRef}
        className={`
          flex-1 overflow-y-auto bg-gray-50 border-0 
          ${isFullscreen ? 'fixed inset-4 z-50 bg-white' : ''}
        `}
        style={isFullscreen ? { maxHeight: 'calc(100vh - 200px)' } : {}}
      >
        <div className="p-8">
          <div className="prose prose-lg max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-gray-700 leading-relaxed">
              {content}
            </pre>
          </div>
        </div>
      </div>
      
      <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-white">
        <div className="flex items-center space-x-3 mb-6">
          <Checkbox
            id="accept-terms"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked as boolean)}
          />
          <label
            htmlFor="accept-terms"
            className="text-sm font-medium leading-none text-gray-700"
          >
            {acceptLabel}
          </label>
        </div>
        
        <div className="flex justify-between gap-4">
          {!isFirstStep ? (
            <Button
              onClick={onBack}
              variant="outline"
              className="px-8"
            >
              Back
            </Button>
          ) : (
            <div></div>
          )}
          <Button
            onClick={handleAccept}
            className="bg-vitl-green hover:bg-vitl-green/90 text-white px-8"
            disabled={!accepted}
          >
            Accept and Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsViewer;